// ─────────────────────────────────────────────
//  RoomCard styles — light theme per the UI-SPEC Room Card Contract.
//  Extracted from RoomCard.js to keep the component under the 500-line limit.
// ─────────────────────────────────────────────
import tokens from "../theme/tokens";

const { color, space, radius, shadow, font } = tokens;

const ON_BREAK_ACCENT = "#f97316"; // orange — matches the breakStrip indicator

// Responsive-scale helper (Prompt 5). Multiplies a base px size by the global
// --atc-scale factor, which the TV board sets from the monitor resolution (and
// an optional manual master slider). Off the TV, the var is unset → resolves to
// 1 → sizes are byte-for-byte identical, so the normal board is untouched.
// Applying it to card min-height, fonts, avatar/icon sizes AND paddings makes
// every element grow/shrink together, keeping the card visually harmonious.
const sc = (px) => `calc(${px}px * var(--atc-scale, 1))`;

const roomCardStyles = {
  card: {
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.lg,
    boxShadow: shadow.sm,
    overflow: "hidden",
    fontFamily: font.family,
    // Pin to the slot so an emergency card (with its wider action bar) can't
    // outgrow its neighbours and overlap them.
    width: "100%",
    boxSizing: "border-box",
    // Uniform card length via minHeight (NOT a hard height): an idle card is
    // exactly this tall so sections line up, but a card that opens an in-card
    // panel — Break "who's covering", the ⏱ details panel — GROWS to fit it
    // instead of clipping it under overflow:hidden. cardBody flex:1 fills.
    // 320 (was 300) buys the always-rendered times/supervisor/footer rows +
    // the fixed message slot; TV mode auto-scales the board to the screen, so
    // every room still fits regardless of card height.
    minHeight: 320,
    display: "flex",
    flexDirection: "column",
  },
  cardHead: {
    padding: "10px 12px 6px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: space.sm,
    // Reserve the badge + "leaves HH:MM" lines even when absent so heads on
    // neighbouring cards stay level (fixed-geometry rule).
    minHeight: 58,
    boxSizing: "border-box",
  },
  headRight: {
    display: "flex",
    alignItems: "center",
    gap: space.xs,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  roomName: {
    // One tier above heading (18): room identity + provider are the two
    // glance-critical fields, and the old 18-vs-14 ramp was too flat to read
    // from across the room / wall TV.
    fontSize: sc(20),
    fontWeight: font.weight.bold,
    color: color.text.primary,
    letterSpacing: "-0.02em",
    lineHeight: 1.15,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  roomLoc: {
    fontSize: sc(font.size.label),
    fontWeight: font.weight.regular,
    color: color.text.muted,
  },
  cardBody: { padding: "6px 12px 10px", flex: 1 },
  // Today's assignment-sheet block — compact label/value rows above the
  // live provider section.
  // Inline case-times strip: START on the left, END on the right.
  assignBlock: {
    background: color.surface.raised,
    border: `1px solid ${color.border.default}`,
    borderRadius: radius.sm,
    padding: "5px 9px",
    marginBottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: space.sm,
  },
  assignTimeItem: { display: "inline-flex", alignItems: "baseline", gap: 5 },
  assignLabel: {
    fontSize: sc(11),
    fontWeight: font.weight.bold,
    color: color.text.muted,
    textTransform: "uppercase",
    letterSpacing: ".5px",
  },
  assignValue: {
    fontSize: sc(14),
    fontWeight: font.weight.semibold,
    color: color.text.secondary,
    fontVariantNumeric: "tabular-nums",
  },
  // Fixed-height person band so the case-time strip below lines up across cards
  // regardless of a 1-, 2-, or 3-line name (the name clamps to 2 lines).
  provRow: { display: "flex", alignItems: "flex-start", gap: 8, marginBottom: space.sm, minHeight: 44 },
  nameClamp: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 2,
    overflow: "hidden",
    lineHeight: 1.15,
  },
  pav: {
    width: sc(24),
    height: sc(24),
    borderRadius: "50%",
    background: "#1e40af14",
    color: color.accent.primary,
    border: "1px solid #1e40af55",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: sc(11),
    fontWeight: font.weight.bold,
  },
  provName: {
    fontSize: sc(15),
    fontWeight: font.weight.semibold,
    color: color.text.primary,
    letterSpacing: "-0.005em",
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    flexWrap: "wrap",
  },
  provRole: { fontSize: sc(font.size.label), color: color.text.muted, fontWeight: font.weight.medium },
  leaveTime: { fontSize: 12, color: color.text.muted, fontWeight: 600, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  // Leave time parked top-right, on its own line under the status pill.
  leaveTimeTop: { flexBasis: "100%", textAlign: "right", fontSize: 11, color: color.text.muted, fontWeight: font.weight.semibold, whiteSpace: "nowrap", marginTop: 2, fontVariantNumeric: "tabular-nums" },
  reliefPending: { width: "100%", fontSize: 11, fontWeight: 800, color: "#b45309", textAlign: "center", lineHeight: 1.3, margin: "2px 0" },
  cardBtnCol: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 64 },
  takeoverBtn: { background: "#16a34a", border: "none", color: "#fff", borderRadius: 6, padding: "4px 10px", minHeight: 24, fontSize: 10, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" },
  reliefLabel: { display: "flex", flexDirection: "column", gap: 4, fontSize: 12, fontWeight: 700, color: color.text.secondary },
  reliefSelect: { padding: "8px 10px", borderRadius: 6, border: `1px solid ${color.border.default}`, fontSize: 13, fontFamily: "inherit", background: "#fff", color: color.text.primary },
  ackBtn: { background: color.accent.error, border: "none", color: "#fff", borderRadius: radius.sm, padding: "5px 12px", fontSize: 12, fontWeight: 800, cursor: "pointer" },
  clearAlertBtn: { background: "#fff", border: `1px solid ${color.accent.error}`, color: color.accent.error, borderRadius: radius.sm, padding: "5px 12px", fontSize: 12, fontWeight: 800, cursor: "pointer" },
  ackedTag: { color: color.accent.error, fontSize: 12, fontWeight: 800, alignSelf: "center" },
  ackNames: { color: color.text.secondary, fontSize: 11, fontWeight: 700, alignSelf: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "60%" },
  supBlock: { display: "flex", flexDirection: "column", gap: 3 },
  supCall: { alignSelf: "flex-start", marginLeft: 0, background: "#e8edf9", color: color.accent.primary, border: `1px solid ${color.accent.primary}`, borderRadius: 10, padding: "0 7px", fontSize: 10, fontWeight: font.weight.bold, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  time: { fontSize: 12, color: color.text.muted },
  divider: { height: 1, background: color.border.default, margin: "8px 0" },
  // Small ⏱ toggle on the right of the provider row — collapses the times +
  // supervisor panel. 44px min touch target via padding for WCAG (UI-SPEC).
  // sc() so the ⇄ Assign-relief / ➜ Next-room buttons follow the TV ⚙ Sizes
  // card slider — fixed 11px/28px was fiddly on wall TVs (user 2026-07-08).
  detailsToggle: {
    flexShrink: 0,
    background: "transparent",
    border: `1px solid ${color.border.default}`,
    color: color.text.muted,
    borderRadius: radius.sm,
    padding: `${sc(4)} ${sc(8)}`,
    fontSize: sc(13),
    lineHeight: 1,
    cursor: "pointer",
    fontFamily: "inherit",
    minHeight: sc(28),
    minWidth: sc(28),
  },
  // Collapsible body holding case times, sign-in time, and supervisor.
  detailsPanel: {
    borderTop: `1px solid ${color.border.default}`,
    paddingTop: space.sm,
    marginBottom: space.sm,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  // sc() so the Surgeon/Supervisor lines follow the TV ⚙ Sizes card slider —
  // fixed 11px was unreadable from across the room (user photo 2026-07-08).
  supRow: { display: "flex", alignItems: "center", gap: 6, fontSize: sc(11), minWidth: 0 },
  supLabel: { color: color.text.muted, flexShrink: 0 },
  // One line, ellipsized — a long doctor name must not change the card height.
  supName: { color: color.text.secondary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 },
  emptyState: {
    textAlign: "center",
    padding: "18px 0",
    color: color.text.muted,
    fontSize: font.size.label,
  },
  relieverTag: {
    color: color.accent.closing,
    fontSize: 11,
    fontWeight: font.weight.bold,
    background: "#f59e0b18",
    border: "1px solid #f59e0b66",
    borderRadius: radius.full,
    padding: "1px 8px",
    animation: "atcFlash 1s steps(1) infinite",
    whiteSpace: "nowrap",
  },
  // Break-coverage bar (Prompts 3 & 9): request / cover / end, on the card.
  breakCoverBar: {
    display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
    marginTop: space.sm, padding: `${sc(6)} ${sc(10)}`,
    background: "#fefce8", border: "1px solid #eab308", borderRadius: radius.sm,
  },
  breakCoverNote: { flex: 1, minWidth: 0, fontSize: sc(11), fontWeight: font.weight.bold, color: "#7a4f00" },
  breakCoverTake: {
    flexShrink: 0, background: "#eab308", border: "none", color: "#3f2d00",
    borderRadius: radius.sm, padding: `${sc(4)} ${sc(12)}`, minHeight: 24, fontSize: sc(11),
    fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap",
  },
  // Fixed message AREA (layout slot 6): quick-message pills + the unified
  // message slot live together in one HARD-height box between the supervisor
  // line and the action buttons. Content scrolls inside; the card NEVER
  // grows or shrinks because of messages.
  messageArea: {
    margin: `0 12px ${space.sm}px`,
    // pills row (20 + its 8px top margin) + 4 gap + 30 slot = 62 exactly.
    height: 62,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    gap: 4,
    overflowY: "auto",
    overflowX: "hidden",
    flexShrink: 0,
  },
  // Unified message slot (domain/cardMessage): one fixed spot at the card's
  // bottom for the single highest-priority status message. Tones reinforce
  // meaning; the text itself always carries it (never color alone).
  messageSlot: {
    margin: 0,
    flexShrink: 0,
    // HARD height (not min): the slot never grows or shrinks, whatever the
    // message type, length, or action buttons — cards stay level.
    height: 30,
    borderRadius: radius.sm,
    padding: "0 9px",
    fontSize: sc(11),
    fontWeight: font.weight.bold,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: 6,
    overflow: "hidden",
  },
  msgEmpty: { border: "1px solid transparent" },
  msgRed: { background: "#fee2e2", border: `1px solid ${color.accent.error}`, color: color.accent.error },
  msgAmber: { background: "#fef8ec", border: "1px solid #f59e0b", color: "#7a4f00" },
  msgNavy: { background: "#1e40af10", border: `1px solid ${color.accent.primary}55`, color: color.accent.primary },
  msgGreen: { background: "#10b98112", border: "1px solid #10b98166", color: "#0a7d5e" },
  msgMuted: { background: color.surface.raised, border: `1px solid ${color.border.default}`, color: color.text.muted, fontStyle: "italic", fontWeight: font.weight.medium },
  // Amber "Cover" pinned right inside the message slot — take over a
  // requested break directly from the message.
  msgCover: {
    flexShrink: 0,
    marginLeft: "auto",
    background: "#eab308",
    border: "none",
    color: "#3f2d00",
    borderRadius: radius.sm,
    padding: "3px 10px",
    minHeight: 22,
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  // Red "Ack" / outlined "Clear" inside the slot for emergency/coverage/urgency.
  msgAck: {
    flexShrink: 0,
    background: color.accent.error,
    border: "none",
    color: "#fff",
    borderRadius: radius.sm,
    padding: "3px 10px",
    minHeight: 22,
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  msgClear: {
    flexShrink: 0,
    background: "#fff",
    border: `1px solid ${color.accent.error}`,
    color: color.accent.error,
    borderRadius: radius.sm,
    padding: "3px 10px",
    minHeight: 22,
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  // Green ✓ pinned right inside the message slot — ends the break in place.
  msgEnd: {
    flexShrink: 0,
    marginLeft: "auto",
    background: "#16a34a",
    border: "none",
    color: "#fff",
    borderRadius: "50%",
    width: 22,
    height: 22,
    fontSize: 12,
    lineHeight: 1,
    fontWeight: 800,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  cardFoot: {
    padding: "6px 12px",
    background: color.surface.raised,
    borderTop: `1px solid ${color.border.default}`,
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    alignItems: "center",
    // Always rendered; fixed band height keeps the card bottom from jumping
    // when a role/state hides the buttons.
    minHeight: 38,
    boxSizing: "border-box",
  },
  cardBtn: {
    background: "transparent",
    border: `1px solid ${color.border.default}`,
    color: color.text.secondary,
    borderRadius: radius.sm,
    padding: `${sc(4)} ${sc(9)}`,
    minHeight: 24, // WCAG 2.5.8 minimum target — the densest board control
    fontSize: sc(11),
    cursor: "pointer",
    fontFamily: "inherit",
  },
  // ── CASE PHASE CELLS (board footer) ───────────────────────────────────────
  // The four phases the room's team records, on the board as well as the phone,
  // writing the SAME `or_cases.phases`. Owner: "this needs to be synchronized
  // with the app", and "the button need to show the name more fade and the time
  // in the top".
  //
  // ⚠️ 2×2, AND THE TIME ON ITS OWN LINE, BECAUSE ONE ROW DOES NOT FIT.
  // The board grid is `minmax(208px, 1fr)`, so the narrowest card gives the
  // footer 184px of content. Four buttons carrying a name and a time on one
  // line measure ~403px — 2.2 rows — and a four-column grid gives each cell
  // 43px against the 66px "Induction" needs. Every one-line layout ends in a
  // truncated phase name, and the name is the whole point of the control.
  // Two lines in a 2×2 measures 136px and clears at every card width.
  // Laid out in CASE_PHASES order, the 2×2 also reads as plain Z-order.
  phaseGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: sc(4),
    width: "100%",
  },
  // ⚠️ FIXED HEIGHT, AND THE TIME LINE IS ALWAYS RENDERED (as "—" when there is
  // none). A cell that grows when somebody presses it makes the card taller,
  // and a card that changes height mid-shift reflows the whole board — the same
  // failure as the emergency card that rendered smaller than its neighbours.
  phaseCell: {
    height: sc(34),
    padding: `${sc(2)} ${sc(6)}`,
    boxSizing: "border-box",
    borderRadius: radius.sm,
    display: "grid",
    alignContent: "center",
    justifyItems: "start",
    overflow: "hidden",
    fontFamily: "inherit",
    textAlign: "left",
    width: "100%",
    cursor: "pointer",
    // ⚠️ THE FULL SHORTHAND IN EVERY VARIANT. The first cut set `border` here
    // and `borderStyle`/`borderColor` in the variant, which only worked because
    // the spread order happened to be right — reorder it and the state silently
    // vanishes. Each variant below restates the whole shorthand.
    border: `1px dashed ${color.border.default}`,
    background: "transparent",
  },
  // A phase already recorded, earlier in the case.
  phaseCellDone: {
    border: `1px solid ${color.border.default}`,
    background: color.surface.card,
  },
  // ⚠️ THE FURTHEST-ALONG STAMPED PHASE — where the case IS. Exactly one cell on
  // a card ever carries it, so scanning the board tells you two things at once:
  // how many cells are filled (how far in) and where the solid one sits (which
  // phase).
  //
  // ⚠️ NAVY, NOT AMBER, AND THAT IS NOT A PREFERENCE. Amber on this card already
  // means break requested, break covering, and the `closing` room status; red
  // means emergency and urgency. Four routine controls on every card all day,
  // tinted with alarm colour, is precisely how staff stop reading alarm colour.
  // `accent.primary` is the app's informational hue (CTA, focus, avatar) and is
  // in no alarm vocabulary. White on #1e40af is 8.72:1.
  //
  // ⚠️ AND THE PHASE NAMED "Closing" IS NOT AMBER EITHER, however much the
  // `closing` STATUS is. Same neutral→navy ramp as the other three.
  phaseCellCurrent: {
    border: `1px solid ${color.accent.primary}`,
    background: color.accent.primary,
  },
  // Read-only, for a viewer the rules would refuse. A span, never a disabled
  // button: they were never offered the control, and a refusal notice for an
  // affordance that was never there is noise.
  phaseCellReadOnly: {
    border: `1px solid ${color.border.default}`,
    background: "transparent",
    cursor: "default",
  },
  // THE TIME IS THE TOP LINE and the loud one — it is what the board is being
  // told. Tabular numerals so four times in a column line up.
  phaseTime: {
    fontSize: sc(12),
    lineHeight: 1.05,
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    color: color.text.primary,
  },
  phaseTimeEmpty: { color: color.text.muted, fontWeight: 700 },
  phaseTimeOnFill: { color: "#ffffff" },
  // The name is the FADED second line — asked for, and right: once a phase is
  // recorded the reader wants the hour, and the name is only the label for it.
  phaseName: {
    fontSize: sc(11),
    lineHeight: 1.05,
    fontWeight: 600,
    whiteSpace: "nowrap",
    color: color.text.secondary,
  },
  phaseNameOnFill: { color: "#ffffff" },
  clearBtn: {
    marginLeft: "auto",
    background: "#dc262610",
    border: "1px solid #dc262655",
    color: color.accent.error,
    borderRadius: radius.sm,
    width: sc(24),
    height: sc(24),
    fontSize: sc(12),
    lineHeight: 1,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  breakBtn: {
    background: "#f59e0b14",
    border: "1px solid #f59e0b55",
    color: "#7a4f00",
    borderRadius: radius.sm,
    padding: `${sc(3)} ${sc(10)}`,
    fontSize: sc(11),
    fontWeight: font.weight.bold,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  breakBtnActive: { animation: "atcFlash 1s steps(1) infinite" },
  breakPanel: {
    borderTop: `1px solid ${color.border.default}`,
    padding: "10px 14px 12px",
    background: color.surface.raised,
  },
  breakPanelLabel: {
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.muted,
    textTransform: "uppercase",
    letterSpacing: ".5px",
    marginBottom: 6,
  },
  breakCoveringNote: {
    fontSize: font.size.label,
    color: "#7a4f00",
    fontWeight: font.weight.bold,
    marginBottom: space.sm,
  },
  // ⚠️ NAVY, NOT THE AMBER `breakCoveringNote` USES. Amber is this card's word
  // for "a reliever is standing in this room", and the whole point of the
  // assigned state is that nobody is.
  breakAwaitingNote: {
    fontSize: font.size.label,
    color: "#1e3a8a",
    fontWeight: font.weight.bold,
    marginBottom: space.sm,
  },
  breakEmptyNote: { fontSize: 11, color: color.text.muted },
  breakChip: {
    background: "transparent",
    border: `1px solid ${color.border.default}`,
    color: color.text.secondary,
    borderRadius: radius.lg,
    padding: "5px 12px",
    minHeight: 24,
    fontSize: 11,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  breakChipActive: {
    background: "#f59e0b14",
    borderColor: color.accent.closing,
    color: "#7a4f00",
    fontWeight: font.weight.bold,
  },
  select: {
    width: "100%",
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    color: color.text.primary,
    borderRadius: radius.sm,
    padding: "9px 12px",
    fontSize: 13,
    fontFamily: "inherit",
    boxSizing: "border-box",
  },
};

export { ON_BREAK_ACCENT, sc };
export default roomCardStyles;
